/**
 * SCRUM-241 — Structura Picking: preparación de un pedido.
 *
 * Marcar líneas usa POST /api/picking/pick (cantidad preparada por línea).
 * Cerrar la preparación usa el PATCH /api/orders existente: la PWA propone el
 * siguiente estado que el motor de transiciones ya permite y deja que el backend
 * decida. No hay estados nuevos ni máquina de estados propia acá.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Loader2, MinusCircle, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PickingShell } from "@/components/picking/PickingShell";
import { ScanField } from "@/components/picking/ScanField";
import { pickingJson } from "@/lib/picking/session";
import {
  allowedOrderTransitions,
  ORDER_STATUS_LABELS,
  type OrderStatus,
} from "@/domain/sm-models";

export const Route = createFileRoute("/picking/pedidos/$orderId")({
  component: PedidoDetailPage,
  head: () => ({ meta: [{ title: "Preparar pedido — Structura Picking" }] }),
});

interface OrderItem {
  id: string;
  productId: string | null;
  productName: string;
  cantidad: number;
  unidadMedida: string;
  variantId: string | null;
  variantName: string | null;
  qtyPicked: number | null;
}

interface OrderDetail {
  id: string;
  orderNumber: number | null;
  customerName: string;
  status: OrderStatus;
  notes?: string | null;
  items: OrderItem[];
}

/** El primer estado permitido que representa "preparación terminada". */
const COMPLETION_TARGETS: OrderStatus[] = ["LISTO", "LISTO_RETIRO", "EN_CAMINO"];

function itemLabel(item: OrderItem): string {
  return item.variantName ? `${item.productName} · ${item.variantName}` : item.productName;
}

function PedidoDetailPage() {
  const { orderId } = Route.useParams();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyItem, setBusyItem] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);
  const [shortageFor, setShortageFor] = useState<string | null>(null);
  const [shortageQty, setShortageQty] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await pickingJson<OrderDetail>(
        `/api/orders?id=${encodeURIComponent(orderId)}`,
      );
      setOrder(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo cargar el pedido");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    void load();
  }, [load]);

  const setPicked = useCallback(
    async (item: OrderItem, qtyPicked: number | null) => {
      setBusyItem(item.id);
      try {
        const data = await pickingJson<{ item: { qtyPicked: number | null } }>(
          "/api/picking/pick",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ orderId, itemId: item.id, qtyPicked }),
          },
        );
        setOrder((prev) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((i) =>
                  i.id === item.id ? { ...i, qtyPicked: data.item.qtyPicked } : i,
                ),
              }
            : prev,
        );
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "No se pudo guardar");
      } finally {
        setBusyItem(null);
      }
    },
    [orderId],
  );

  /** Resuelve un código escaneado contra las líneas del pedido. */
  const handleCode = useCallback(
    async (code: string) => {
      if (!order) return;
      let matched: OrderItem | undefined;

      try {
        const data = await pickingJson<{
          match?: { productId: string; variantId: string | null };
        }>(`/api/picking/lookup?code=${encodeURIComponent(code)}`);
        if (data.match) {
          matched = order.items.find(
            (i) =>
              i.productId === data.match!.productId &&
              (data.match!.variantId == null || i.variantId === data.match!.variantId),
          );
          if (!matched) {
            toast.error("Ese producto no pertenece a este pedido");
            return;
          }
        }
      } catch {
        // El código no resolvió como EAN/SKU: caemos a búsqueda por nombre.
      }

      if (!matched) {
        const needle = code.toLowerCase();
        const hits = order.items.filter((i) => itemLabel(i).toLowerCase().includes(needle));
        if (hits.length === 0) {
          toast.error("Sin coincidencias en este pedido");
          return;
        }
        if (hits.length > 1) {
          toast.error("Varias coincidencias: afiná la búsqueda");
          return;
        }
        matched = hits[0];
      }

      if (matched.qtyPicked != null) {
        toast.info(`${itemLabel(matched)} ya estaba preparado`);
        return;
      }
      await setPicked(matched, matched.cantidad);
      toast.success(`${itemLabel(matched)} preparado`);
    },
    [order, setPicked],
  );

  const progress = useMemo(() => {
    const items = order?.items ?? [];
    return {
      done: items.filter((i) => i.qtyPicked != null).length,
      total: items.length,
      missing: items.filter((i) => i.qtyPicked != null && i.qtyPicked < i.cantidad).length,
    };
  }, [order]);

  const completionTarget = useMemo(() => {
    if (!order) return null;
    const allowed = allowedOrderTransitions(order.status);
    return COMPLETION_TARGETS.find((s) => allowed.includes(s)) ?? null;
  }, [order]);

  const finish = useCallback(async () => {
    if (!order || !completionTarget) return;
    setClosing(true);
    try {
      await pickingJson(`/api/orders?id=${encodeURIComponent(order.id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: completionTarget }),
      });
      toast.success(`Pedido marcado como ${ORDER_STATUS_LABELS[completionTarget]}`);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo cerrar la preparación");
    } finally {
      setClosing(false);
    }
  }, [completionTarget, load, order]);

  return (
    <PickingShell
      title={order ? `Pedido #${order.orderNumber ?? "—"}` : "Pedido"}
      backTo="/picking/pedidos"
    >
      {() =>
        loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !order ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            Pedido no encontrado.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">
                  {order.customerName}
                </p>
                <p className="text-xs text-muted-foreground">
                  {ORDER_STATUS_LABELS[order.status]} · {progress.done}/{progress.total} preparados
                  {progress.missing > 0 ? ` · ${progress.missing} con faltante` : ""}
                </p>
              </div>
            </div>

            {order.notes ? (
              <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                {order.notes}
              </p>
            ) : null}

            <ScanField onCode={(code) => void handleCode(code)} />

            <div className="space-y-2">
              {order.items.map((item) => {
                const picked = item.qtyPicked != null;
                const shortage = picked && item.qtyPicked! < item.cantidad;
                return (
                  <div
                    key={item.id}
                    className={`rounded-lg border px-3 py-2.5 ${
                      shortage
                        ? "border-amber-500/40 bg-amber-500/5"
                        : picked
                          ? "border-border/50 bg-muted/30"
                          : "border-border bg-card"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p
                          className={`truncate text-sm font-medium ${
                            picked ? "text-muted-foreground line-through" : "text-foreground"
                          }`}
                        >
                          {itemLabel(item)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {item.cantidad} {item.unidadMedida}
                          {shortage ? ` · preparado ${item.qtyPicked}` : ""}
                        </p>
                      </div>
                      {shortage ? (
                        <Badge variant="outline" className="shrink-0 text-[10px]">
                          Faltante
                        </Badge>
                      ) : null}
                      {busyItem === item.id ? (
                        <Loader2 className="h-5 w-5 shrink-0 animate-spin text-muted-foreground" />
                      ) : picked ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-9 w-9 shrink-0"
                          onClick={() => void setPicked(item, null)}
                          aria-label={`Desmarcar ${itemLabel(item)}`}
                        >
                          <Undo2 className="h-4 w-4" />
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-9 w-9 shrink-0"
                            onClick={() => {
                              setShortageFor(item.id);
                              setShortageQty("");
                            }}
                            aria-label={`Registrar faltante de ${itemLabel(item)}`}
                          >
                            <MinusCircle className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            className="h-9 w-9 shrink-0"
                            onClick={() => void setPicked(item, item.cantidad)}
                            aria-label={`Marcar ${itemLabel(item)} como preparado`}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>

                    {shortageFor === item.id ? (
                      <div className="mt-2 flex items-center gap-2">
                        <Input
                          value={shortageQty}
                          onChange={(e) => setShortageQty(e.target.value)}
                          placeholder={`Cantidad real (pedido: ${item.cantidad})`}
                          inputMode="decimal"
                          className="h-9"
                          autoFocus
                        />
                        <Button
                          size="sm"
                          onClick={() => {
                            const qty = Number(shortageQty.replace(",", "."));
                            if (!Number.isFinite(qty) || qty < 0 || qty > item.cantidad) {
                              toast.error(`Ingresá un valor entre 0 y ${item.cantidad}`);
                              return;
                            }
                            setShortageFor(null);
                            void setPicked(item, qty);
                          }}
                        >
                          Guardar
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setShortageFor(null)}>
                          Cancelar
                        </Button>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {completionTarget ? (
              <Button
                className="w-full"
                size="lg"
                disabled={closing || progress.done < progress.total}
                onClick={() => void finish()}
              >
                {closing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {progress.done < progress.total
                  ? `Faltan ${progress.total - progress.done} líneas`
                  : `Marcar como ${ORDER_STATUS_LABELS[completionTarget]}`}
              </Button>
            ) : (
              <p className="text-center text-xs text-muted-foreground">
                Este pedido no admite avanzar la preparación desde acá.
              </p>
            )}
          </div>
        )
      }
    </PickingShell>
  );
}
